// src/pages/Reports/ReceiptReports/ReceiptReportBase.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";

import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";


import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import { fetchAgentsThunk } from "../../../store/slices/agentsSlice";

import {
  fetchReceiptReportThunk,
  exportReceiptReportThunk,
  clearReceiptReport,
} from "../../../store/slices/receiptReportSlice";

import "./ReportPage.css";

// =====================================================
// TITLES
// =====================================================

const titleByScope = {
  all: "Receipt - Report",
  water: "Water Receipt - Report",
  slrm: "SLRM Receipt - Report",
};

const ReceiptReportBase = ({ scope = "all" }) => {
  const dispatch = useDispatch();

  // =====================================================
  // REDUX
  // =====================================================

  const wards = useSelector(
    (state) => state.wards?.list || [],
  );

  const agents = useSelector(
    (state) => state.agents?.list || [],
  );

  const {
    rows = [],
    loading = false,
    error = null,

    exportLoading = false,
    exportError = null,
    exportFormat = null,
  } = useSelector(
    (state) => state.receiptReport || {},
  );

  // =====================================================
  // STATE
  // =====================================================

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [wardId, setWardId] = useState("");
  const [agentId, setAgentId] = useState("");

  const [search, setSearch] = useState("");

  const [dateError, setDateError] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] =
    useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // =====================================================
  // COLUMN VISIBILITY
  // =====================================================

  const showWaterCol =
    scope === "all" || scope === "water";

  const showSlrmCol =
    scope === "all" || scope === "slrm";

  // =====================================================
  // SINGLE COLUMN CONFIG
  //
  // UI = EXCEL = PDF
  // =====================================================

  const reportColumns = useMemo(() => {
    const columns = [
      {
        key: "serial",
        sortKey: "rct_no",
        label: "#",
        excelWidth: 8,
        pdfWidth: "4%",
      },

      {
        key: "rct_no",
        sortKey: "rct_no",
        label: "Receipt No",
        excelWidth: 14,
        pdfWidth: "6%",
      },

      {
        key: "qr_code",
        sortKey: "qr_code",
        label: "House Code",
        excelWidth: 16,
        pdfWidth: "7%",
      },

      {
        key: "door_no",
        sortKey: "door_no",
        label: "Door No",
        excelWidth: 16,
        pdfWidth: "7%",
      },

      {
        key: "family_head",
        sortKey: "family_head",
        label: "Name",
        excelWidth: 36,
        pdfWidth: "15%",
        kannada: true,
      },

      {
        key: "ward_name",
        sortKey: "ward_name",
        label: "Ward",
        excelWidth: 20,
        pdfWidth: "7%",
        kannada: true,
      },

      {
        key: "rct_type",
        sortKey: "rct_type",
        label: "Rct Type",
        excelWidth: 16,
        pdfWidth: "6%",
      },

      {
        key: "man_rct_no",
        sortKey: "man_rct_no",
        label: "Manual RCT No",
        excelWidth: 18,
        pdfWidth: "7%",
      },
    ];

    // =================================================
    // WATER COLUMN
    // =================================================

    if (showWaterCol) {
      columns.push({
        key: "water_fee",
        sortKey: "water_fee",
        label: "Water Charge",
        excelWidth: 16,
        pdfWidth: "7%",
        numeric: true,
      });
    }

    // =================================================
    // SLRM COLUMN
    // =================================================

    if (showSlrmCol) {
      columns.push({
        key: "slrm_fee",
        sortKey: "slrm_fee",
        label: "SLRM Fee",
        excelWidth: 14,
        pdfWidth: "6%",
        numeric: true,
      });
    }

    // =================================================
    // COMMON END COLUMNS
    // =================================================

    columns.push(
      {
        key: "amount",
        sortKey: "amount",
        label: "Amount",
        excelWidth: 14,
        pdfWidth: "6%",
        numeric: true,
      },

      {
        key: "rct_date",
        sortKey: "rct_date",
        label: "Receipt Date",
        excelWidth: 16,
        pdfWidth: "8%",
      },

      {
        key: "mode",
        sortKey: "mode",
        label: "Mode",
        excelWidth: 14,
        pdfWidth: "6%",
      },

      {
        key: "remarks",
        sortKey: "remarks",
        label: "Remarks",
        excelWidth: 25,
        pdfWidth: "10%",
        kannada: true,
      },

      {
        key: "generated_by",
        sortKey: "generated_by",
        label: "Generated By",
        excelWidth: 20,
        pdfWidth: "8%",
        kannada: true,
      },
    );

    return columns;
  }, [showWaterCol, showSlrmCol]);

  // =====================================================
  // LOAD MASTER DATA
  // =====================================================

  useEffect(() => {
    dispatch(fetchWardsThunk());
    dispatch(fetchAgentsThunk());
  }, [dispatch]);

  // =====================================================
  // RESET WHEN REPORT TYPE CHANGES
  // =====================================================

  useEffect(() => {
    dispatch(clearReceiptReport());

    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    setDateError("");
  }, [dispatch, scope]);

  // =====================================================
  // CLEAR WHEN UNMOUNTED
  // =====================================================

  useEffect(() => {
    return () => {
      dispatch(clearReceiptReport());
    };
  }, [dispatch]);

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDDMMYYYY = (value) => {
    if (!value) {
      return "";
    }

    const [yyyy, mm, dd] =
      value.split("-");

    return `${dd}-${mm}-${yyyy}`;
  };

  useEffect(() => {
    setDateError("");
  }, [fromDate, toDate]);

  // =====================================================
  // GENERATED BY
  // =====================================================

  const getGeneratedBy = (row) => {
    if (row.user_type === "office") {
      return (
        row.office_username ||
        "Office"
      );
    }

    return (
      row.ag_name ||
      row.generated_name ||
      ""
    );
  };

  // =====================================================
  // VALUE FROM COLUMN
  // =====================================================

  const getColumnValue = (
    row,
    column,
    index = 0,
  ) => {
    if (column.key === "serial") {
      return index + 1;
    }

    if (
      column.key ===
      "generated_by"
    ) {
      return getGeneratedBy(row);
    }

    return (
      row?.[column.key] ??
      ""
    );
  };

  // =====================================================
  // SORT
  // =====================================================

  const handleSort = (field) => {
    setSortField(
      (currentField) => {
        if (
          currentField === field
        ) {
          setSortDirection(
            (
              currentDirection,
            ) =>
              currentDirection ===
              "asc"
                ? "desc"
                : "asc",
          );

          return currentField;
        }

        setSortDirection(
          "asc",
        );

        return field;
      },
    );

    setPage(1);
  };

  // =====================================================
  // SORT ICON
  // =====================================================

  const renderSortIcon = (
    field,
  ) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp
            size={11}
          />

          <ChevronDown
            size={11}
          />
        </span>
      );
    }

    return sortDirection ===
      "asc" ? (
      <ChevronUp
        size={14}
        className="ms-1"
      />
    ) : (
      <ChevronDown
        size={14}
        className="ms-1"
      />
    );
  };

  // =====================================================
  // SEARCH + SORT
  // =====================================================

  const filteredRows =
    useMemo(() => {
      const query = search
        .trim()
        .toLowerCase();

      const searchedRows =
        !query
          ? [...rows]
          : rows.filter(
              (row) => {
                const generatedBy =
                  getGeneratedBy(
                    row,
                  );

                const searchable =
                  `
                    ${row.rct_no || ""}
                    ${row.qr_code || ""}
                    ${row.door_no || ""}
                    ${row.family_head || ""}
                    ${row.ward_name || ""}
                    ${row.rct_type || ""}
                    ${row.man_rct_no || ""}
                    ${row.water_fee ?? ""}
                    ${row.slrm_fee ?? ""}
                    ${row.amount ?? ""}
                    ${row.rct_date || ""}
                    ${row.mode || ""}
                    ${row.remarks || ""}
                    ${generatedBy}
                  `.toLowerCase();

                return searchable.includes(
                  query,
                );
              },
            );

      if (!sortField) {
        return searchedRows;
      }

      const numericFields = [
        "rct_no",
        "water_fee",
        "slrm_fee",
        "amount",
      ];

      return [
        ...searchedRows,
      ].sort((a, b) => {
        let aValue;
        let bValue;

        if (
          sortField ===
          "generated_by"
        ) {
          aValue =
            getGeneratedBy(a);

          bValue =
            getGeneratedBy(b);
        } else {
          aValue =
            a?.[sortField];

          bValue =
            b?.[sortField];
        }

        if (
          numericFields.includes(
            sortField,
          )
        ) {
          const aNumber =
            Number(
              aValue || 0,
            );

          const bNumber =
            Number(
              bValue || 0,
            );

          return sortDirection ===
            "asc"
            ? aNumber -
                bNumber
            : bNumber -
                aNumber;
        }

        if (
          sortField ===
          "rct_date"
        ) {
          const aTime =
            aValue
              ? new Date(
                  aValue,
                ).getTime()
              : 0;

          const bTime =
            bValue
              ? new Date(
                  bValue,
                ).getTime()
              : 0;

          return sortDirection ===
            "asc"
            ? aTime -
                bTime
            : bTime -
                aTime;
        }

        const comparison =
          String(
            aValue || "",
          ).localeCompare(
            String(
              bValue || "",
            ),
            undefined,
            {
              numeric: true,
              sensitivity:
                "base",
            },
          );

        return sortDirection ===
          "asc"
          ? comparison
          : -comparison;
      });
    }, [
      rows,
      search,
      sortField,
      sortDirection,
    ]);

  // =====================================================
  // RESET PAGE
  // =====================================================

  useEffect(() => {
    setPage(1);
  }, [
    fromDate,
    toDate,
    wardId,
    agentId,
    search,
    rows.length,
  ]);

  // =====================================================
  // PAGINATION
  // =====================================================

  const total =
    filteredRows.length;

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        total / pageSize,
      ),
    );

  const currentPage =
    Math.min(
      page,
      totalPages,
    );

  const pagedRows =
    useMemo(() => {
      const start =
        (currentPage - 1) *
        pageSize;

      return filteredRows.slice(
        start,
        start + pageSize,
      );
    }, [
      filteredRows,
      currentPage,
      pageSize,
    ]);

  // =====================================================
  // LOAD
  // =====================================================

  const handleLoad = () => {
    setDateError("");

    dispatch(
      fetchReceiptReportThunk({
        from_date: fromDate,
        to_date: toDate,
        ward_id: wardId,
        agent_id: agentId,
        scope,
      }),
    );
  };

  // =====================================================
  // CLEAR
  // =====================================================

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setWardId("");
    setAgentId("");
    setSearch("");

    setSortField("");
    setSortDirection("asc");

    setPage(1);
    setDateError("");

    dispatch(
      clearReceiptReport(),
    );
  };

  // =====================================================
  // BACKEND EXPORT
  // Excel + PDF are generated on backend.
  // Large datasets do not load into html2canvas/jsPDF.
  // =====================================================

  const exportExcel = () => {
    if (
      exportLoading ||
      !rows.length
    ) {
      return;
    }

    dispatch(
      exportReceiptReportThunk({
        format: "excel",

        from_date: fromDate,
        to_date: toDate,
        ward_id: wardId,
        agent_id: agentId,

        scope,

        search,

        sort_field:
          sortField ||
          "rct_no",

        sort_direction:
          sortField
            ? sortDirection
            : "desc",
      }),
    );
  };

  const exportPDF = () => {
    if (
      exportLoading ||
      !rows.length
    ) {
      return;
    }

    dispatch(
      exportReceiptReportThunk({
        format: "pdf",

        from_date: fromDate,
        to_date: toDate,
        ward_id: wardId,
        agent_id: agentId,

        scope,

        search,

        sort_field:
          sortField ||
          "rct_no",

        sort_direction:
          sortField
            ? sortDirection
            : "desc",
      }),
    );
  };

  // =====================================================
  // PAGINATION UI
  // =====================================================

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const items = [];
    const maxButtons = 5;

    let start =
      Math.max(
        1,
        currentPage -
          Math.floor(
            maxButtons / 2,
          ),
      );

    let end =
      Math.min(
        totalPages,
        start +
          maxButtons -
          1,
      );

    start =
      Math.max(
        1,
        end -
          maxButtons +
          1,
      );

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={
          currentPage === 1
        }
        onClick={() =>
          setPage(
            (previous) =>
              Math.max(
                1,
                previous - 1,
              ),
          )
        }
      />,
    );

    for (
      let pageNumber = start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      items.push(
        <Pagination.Item
          key={pageNumber}
          active={
            pageNumber ===
            currentPage
          }
          onClick={() =>
            setPage(
              pageNumber,
            )
          }
        >
          {pageNumber}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage ===
          totalPages
        }
        onClick={() =>
          setPage(
            (previous) =>
              Math.min(
                totalPages,
                previous + 1,
              ),
          )
        }
      />,
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="report-page">
      <Card className="report-card">

        {/* HEADER */}

        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                {titleByScope[
                  scope
                ] || "Report"}
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>

          {/* ERROR */}

          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          {exportError ? (
            <div className="alert alert-danger mb-3">
              {exportError}
            </div>
          ) : null}

          {dateError ? (
            <div className="alert alert-warning mb-3">
              {dateError}
            </div>
          ) : null}

          {/* ========================================= */}
          {/* FILTERS                                   */}
          {/* ========================================= */}

          <div className="report-filter-box">
            <Row className="g-3">

              {/* FROM DATE */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(
                    event,
                  ) =>
                    setFromDate(
                      event.target
                        .value,
                    )
                  }
                />

                <small className="text-muted">
                  {fromDate
                    ? formatDDMMYYYY(
                        fromDate,
                      )
                    : "dd-mm-yyyy"}
                </small>
              </Col>

              {/* TO DATE */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(
                    event,
                  ) =>
                    setToDate(
                      event.target
                        .value,
                    )
                  }
                />

                <small className="text-muted">
                  {toDate
                    ? formatDDMMYYYY(
                        toDate,
                      )
                    : "dd-mm-yyyy"}
                </small>
              </Col>

              {/* WARD */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={wardId}
                  onChange={(
                    event,
                  ) =>
                    setWardId(
                      event.target
                        .value,
                    )
                  }
                >
                  <option value="">
                    Select Ward
                  </option>

                  {wards.map(
                    (ward) => (
                      <option
                        key={
                          ward.ward_id
                        }
                        value={String(
                          ward.ward_id,
                        )}
                      >
                        {
                          ward.ward_name
                        }
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>

              {/* AGENT */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Agent
                </Form.Label>

                <Form.Select
                  value={agentId}
                  onChange={(
                    event,
                  ) =>
                    setAgentId(
                      event.target
                        .value,
                    )
                  }
                >
                  <option value="">
                    Select Agent
                  </option>

                  <option value="office">
                    Office
                  </option>

                  {agents.map(
                    (agent) => (
                      <option
                        key={
                          agent.agent_id
                        }
                        value={String(
                          agent.agent_id,
                        )}
                      >
                        {
                          agent.ag_name
                        }
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>

              {/* BUTTONS */}

              <Col
                lg={12}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">

                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear
                  </Button>

                </div>
              </Col>

            </Row>
          </div>

          {/* ========================================= */}
          {/* TOOLBAR                                   */}
          {/* ========================================= */}

          <div className="report-table-toolbar">

            <div className="report-table-title">
              Records{" "}

              <span className="report-count">
                (
                {total.toLocaleString()}{" "}
                records)
              </span>
            </div>

            <div className="report-table-right">

              {/* SEARCH */}

              <InputGroup className="report-search">

                <InputGroup.Text>
                  <Search
                    size={16}
                  />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(
                    event,
                  ) => {
                    setSearch(
                      event.target
                        .value,
                    );

                    setPage(1);
                  }}
                  placeholder="Search receipt / house / name / ward / agent..."
                />

              </InputGroup>

              {/* EXCEL */}

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={
                  exportExcel
                }
                disabled={
                  !filteredRows.length ||
                  exportLoading
                }
              >
                {exportLoading &&
                exportFormat === "excel" ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading &&
                exportFormat === "excel"
                  ? "Exporting..."
                  : "Excel"}
              </Button>

              {/* PDF */}

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={
                  exportPDF
                }
                disabled={
                  !filteredRows.length ||
                  exportLoading
                }
              >
                {exportLoading &&
                exportFormat === "pdf" ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading &&
                exportFormat === "pdf"
                  ? "Exporting..."
                  : "PDF"}
              </Button>

            </div>
          </div>

          {/* ========================================= */}
          {/* TABLE                                     */}
          {/* ========================================= */}

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                {/* HEADER */}

                <thead>
                  <tr>
                    {reportColumns.map(
                      (column) => (
                        <th
                          key={
                            column.key
                          }
                        >
                          <button
                            type="button"
                            className={`report-sort-btn ${
                              sortField ===
                              column.sortKey
                                ? "is-active"
                                : ""
                            }`}
                            onClick={() =>
                              handleSort(
                                column.sortKey,
                              )
                            }
                          >
                            {
                              column.label
                            }

                            {renderSortIcon(
                              column.sortKey,
                            )}
                          </button>
                        </th>
                      ),
                    )}
                  </tr>
                </thead>

                {/* BODY */}

                <tbody>

                  {loading ? (
                    <tr>
                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (

                    pagedRows.map(
                      (
                        row,
                        index,
                      ) => {

                        const globalIndex =
                          (currentPage -
                            1) *
                            pageSize +
                          index;

                        return (
                          <tr
                            key={String(
                              row.rct_no ||
                                globalIndex,
                            )}
                          >
                            {reportColumns.map(
                              (
                                column,
                              ) => (
                                <td
                                  key={
                                    column.key
                                  }
                                >
                                  {getColumnValue(
                                    row,
                                    column,
                                    globalIndex,
                                  )}
                                </td>
                              ),
                            )}
                          </tr>
                        );
                      },
                    )

                  ) : (
                    <tr>
                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}

                </tbody>

              </Table>

            </div>

            {/* ======================================= */}
            {/* PAGINATION                              */}
            {/* ======================================= */}

            <div className="report-pagination-bar">

              <div className="report-page-size">

                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  onChange={(
                    event,
                  ) => {
                    setPage(1);

                    setPageSize(
                      Number(
                        event.target
                          .value,
                      ),
                    );
                  }}
                  className="page-size-select"
                >
                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (size) => (
                      <option
                        key={
                          size
                        }
                        value={
                          size
                        }
                      >
                        {size}
                      </option>
                    ),
                  )}
                </Form.Select>

              </div>

              <div className="report-pagination">
                {totalPages > 1
                  ? renderPagination()
                  : null}
              </div>

              <div className="report-page-info">
                Page{" "}
                {currentPage} /{" "}
                {totalPages}
              </div>

            </div>

          </div>

        </Card.Body>

      </Card>
    </div>
  );
};

export default ReceiptReportBase;